import type { MyWalletScreenState } from '~/features/my-wallet/types/screen-state'
import useWalletPaymentModel, { isWalletForbiddenError } from '~/store-v2/my-wallet/useWalletPaymentModel'
import { useMyWalletPartner } from './useMyWalletPartner'

/**
 * Shared by all wallet tabs, which read the same `wallet_payment_model` cache key.
 * Whole-screen error only when there is no cached data: a failed refetch keeps the content.
 */
export function useMyWalletScreenState() {
  const { partnerId, isAdminNotImpersonating, openImpersonateModal } = useMyWalletPartner()

  const {
    data: paymentModel,
    isLoading,
    isError,
    error,
    refetch,
  } = useWalletPaymentModel({
    partnerId,
    enabled: computed(() => !isAdminNotImpersonating.value),
  })

  const screenState = computed<MyWalletScreenState>(() => {
    if (isAdminNotImpersonating.value)
      return 'not_impersonating'
    if (isError.value && !paymentModel.value)
      return isWalletForbiddenError(error.value) ? 'forbidden' : 'error'
    if (isLoading.value)
      return 'loading'
    // The basic profile has no access to the financing portal; the block-at-login feature is pending.
    if (paymentModel.value?.eligibility_context?.partner_profile === 'basic')
      return 'forbidden'
    return paymentModel.value ? 'ready' : 'empty'
  })

  async function handleRefresh() {
    await refetch()
  }

  return {
    paymentModel,
    screenState,
    handleRefresh,
    openImpersonateModal,
  }
}
